import { apiClient, getCountryOptions, getPaypalApprovalDetails, isRecord, logger } from '@kuow/core';
import type { DonationForm, PaypalCheckoutKind } from '@kuow/core';
import type { FormData } from './types';
import { paypalErrorDiagnostic } from './paypalDiagnostics';

export type PaypalKind = PaypalCheckoutKind;
export interface PaypalApproval {
  id: string;
  kind: PaypalKind;
  terms: string;
  fundingSource?: string;
}
export type PaypalDonorFields = Partial<Pick<FormData,
  'firstName' | 'lastName' | 'email' | 'street' | 'city' | 'state' | 'country' | 'postalCode'>>;

export function requiresPaypalAddress(data: FormData, form: DonationForm): boolean {
  // Billing updates only replace payment details; they never collect an address.
  if (data.billingDonationId !== undefined) return false;
  return (data.gift !== null && !data.noGift) || form.always_show_address;
}

export function paypalTerms(data: FormData, form: DonationForm): string {
  return JSON.stringify([
    form.id, form.paypal_client_id, data.frequency, data.totalDonationAmount ?? data.customAmount ?? data.amount,
    data.coverTheCost ? data.coverTheCostAmount : 0, data.coverTheCost, data.gift, data.noGift,
    requiresPaypalAddress(data, form), data.billingDonationId, data.upgradeDonationId, data.billDate,
  ]);
}

export function hasPaypalApproval(data: FormData, form: DonationForm): boolean {
  return Boolean(data.paypalApproval && data.paypalApproval.terms === paypalTerms(data, form)
    && data.paypalApproval.kind === (data.frequency === 'monthly' ? 'subscription' : 'order'));
}

export async function createPaypalCheckout(data: FormData, form: DonationForm): Promise<PaypalApproval> {
  const kind = data.frequency === 'monthly' ? 'subscription' : 'order';
  const response = await apiClient.post<Record<string, string>>(
    `/donate/v1/paypal/${kind === 'order' ? 'orders' : 'subscriptions'}`,
    { amount: data.totalDonationAmount ?? data.customAmount ?? data.amount,
      requires_shipping: requiresPaypalAddress(data, form), form_id: form.id,
      ...(kind === 'subscription' && data.billDate ? { bill_date: data.billDate } : {}) },
    { timeout: 30_000, withAuth: false },
  );
  const id = response[kind === 'order' ? 'order_id' : 'subscription_id'];
  if (typeof id !== 'string' || !id) throw new Error('Unable to start PayPal checkout. Please try again.');
  return { id, kind, terms: paypalTerms(data, form) };
}

export async function loadPaypalApprovalDonor(approval: PaypalApproval): Promise<PaypalDonorFields> {
  try {
    const details = await getPaypalApprovalDetails(approval.kind, approval.id);
    const idKey = approval.kind === 'order' ? 'order_id' : 'subscription_id';
    if (!isRecord(details) || details[idKey] !== approval.id || details.status !== 'APPROVED'
      || !isRecord(details.donor) || Array.isArray(details.donor)) {
      throw new Error('Unable to read your PayPal approval. Please try again.');
    }
    return paypalDonorFields(details.donor);
  } catch (error) {
    logger.warn('PayPal approval details failed', {
      kind: approval.kind, source: 'core', ...paypalErrorDiagnostic(error),
      online: navigator.onLine, visibility: document.visibilityState,
    });
    throw error;
  }
}

// Core normalizes Orders and Subscriptions to the same minimal donor contract.
export function paypalDonorFields(raw: unknown): PaypalDonorFields {
  if (!isRecord(raw) || Array.isArray(raw)) throw new Error('Unable to read your PayPal approval. Please try again.');
  const fields: PaypalDonorFields = {};
  const mapping = {
    first_name: 'firstName', last_name: 'lastName', email: 'email', street: 'street',
    city: 'city', state: 'state', postal_code: 'postalCode',
  } as const;
  for (const [key, field] of Object.entries(mapping)) {
    const value = raw[key];
    if (typeof value === 'string') fields[field] = value;
  }
  if (typeof raw.country_code === 'string') {
    fields.country = getCountryOptions().find(option => option.value.toUpperCase() === raw.country_code)?.label ?? '';
  }
  return fields;
}

export function prefillPaypalDonor(data: FormData, fields: PaypalDonorFields, knownMember: boolean): FormData {
  const next = { ...data };
  for (const key of ['firstName', 'lastName', 'email'] as const) {
    if (knownMember) continue;
    if (!next[key] && fields[key]) next[key] = fields[key];
  }
  // Keep a donor's entered address together; never mix it with a different
  // PayPal address. The initial country alone is only a default.
  if (!data.street && !data.city && !data.state && !data.postalCode) {
    for (const key of ['street', 'city', 'state', 'postalCode', 'country'] as const) {
      if (fields[key] !== undefined) next[key] = fields[key];
    }
  }
  return next;
}
